import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  StickyNote, Globe, Lock, Copy, CheckCircle2, Eye, AlertTriangle,
  Moon, Sun, Edit3, Save, X, RotateCcw,
} from 'lucide-react';
import { fetchPublicNote, updatePublicNote } from '../api/notepad';

const MAX_CHARS = 10000;

function stripHtml(html) {
  const tmp = document.createElement('div');
  tmp.innerHTML = html || '';
  return tmp.textContent || tmp.innerText || '';
}

function StatusPage({ icon: Icon, color, title, message, showSignIn }) {
  return (
    <div className="np-public-status-page">
      <div className="np-public-status-icon" style={{ color }}>
        <Icon size={48} />
      </div>
      <h1 className="np-public-status-title">{title}</h1>
      <p className="np-public-status-msg">{message}</p>
      {showSignIn && (
        <Link to="/login" className="np-public-signin-btn">Sign In</Link>
      )}
    </div>
  );
}

export default function NotepadPublicPage() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | OK | NOT_FOUND | PRIVATE | EXPIRED | REVOKED | DISABLED | ERROR
  const [note, setNote] = useState(null);
  const [settings, setSettings] = useState(null);
  const [copied, setCopied] = useState(false);
  const [darkMode, setDarkMode] = useState(
    () => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  // Edit Mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!token) { setStatus('NOT_FOUND'); return; }
    fetchPublicNote(token)
      .then((res) => {
        const data = res.data;
        setStatus(data.status);
        if (data.status === 'OK') {
          setNote(data.note);
          setSettings(data.settings);
          setEditTitle(data.note.title || '');
          setEditContent(data.note.content || '');
        }
      })
      .catch((err) => {
        const s = err?.response?.data?.status || 'ERROR';
        setStatus(s);
      });
  }, [token]);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const plainContent = stripHtml(note?.content || '');
  const charCount = plainContent.length;

  const editCharCount = useMemo(() => stripHtml(editContent || '').length, [editContent]);
  const editCharPercent = Math.min((editCharCount / MAX_CHARS) * 100, 100);
  const editCharColor = editCharCount > MAX_CHARS * 0.9 ? '#ef4444' : editCharCount > MAX_CHARS * 0.75 ? '#f59e0b' : '#10b981';

  const handleSave = async () => {
    if (!editTitle.trim()) {
      setSaveError('Title is required');
      return;
    }
    if (editCharCount > MAX_CHARS) {
      setSaveError(`Content exceeds ${MAX_CHARS} characters`);
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      const res = await updatePublicNote(token, {
        title: editTitle.trim(),
        content: editContent,
      });
      setNote(res.data.note);
      if (res.data.settings) setSettings(res.data.settings);
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
        setIsEditing(false);
      }, 1000);
    } catch (err) {
      setSaveError(err?.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditTitle(note?.title || '');
    setEditContent(note?.content || '');
    setSaveError('');
    setIsEditing(false);
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="np-public-loading">
            <div className="np-public-spinner" />
            <p>Loading note...</p>
          </div>
        );
      case 'NOT_FOUND':
        return <StatusPage icon={AlertTriangle} color="#ef4444" title="Note Not Found" message="This note does not exist or has been deleted." showSignIn={false} />;
      case 'PRIVATE':
        return <StatusPage icon={Lock} color="#6366f1" title="This note is private." message="Sign in to check if you have access to this note." showSignIn={true} />;
      case 'EXPIRED':
        return <StatusPage icon={AlertTriangle} color="#f59e0b" title="Link Expired" message="This public link has expired and is no longer accessible." showSignIn={false} />;
      case 'REVOKED':
        return <StatusPage icon={Lock} color="#ef4444" title="Link Revoked" message="The owner has revoked access to this note." showSignIn={false} />;
      case 'DISABLED':
        return <StatusPage icon={EyeOff} color="#6b7280" title="Public Access Disabled" message="The owner has disabled public access to this note." showSignIn={false} />;
      case 'ERROR':
        return <StatusPage icon={AlertTriangle} color="#ef4444" title="Something Went Wrong" message="Unable to load this note. Please try again later." showSignIn={false} />;
      case 'OK':
        return (
          <article className="np-public-article">
            <header className="np-public-article-header">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div className="np-public-badge">
                    <Globe size={13} /> <span>Public Note</span>
                  </div>
                  {settings?.allow_edit && (
                    <div className="np-public-badge" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981' }}>
                      <Edit3 size={13} /> <span>Publicly Editable</span>
                    </div>
                  )}
                </div>

                {settings?.allow_edit && !isEditing && (
                  <button
                    type="button"
                    className="np-public-edit-toggle-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit3 size={14} /> Edit / Write Note
                  </button>
                )}
              </div>

              {isEditing ? (
                <div className="np-public-edit-title-box" style={{ marginTop: 12 }}>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--np-text2)', display: 'block', marginBottom: 4 }}>Note Title</label>
                  <input
                    type="text"
                    className="np-public-title-input"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Note title..."
                    maxLength={500}
                  />
                </div>
              ) : (
                <h1 className="np-public-note-title">{note.title}</h1>
              )}

              <div className="np-public-meta">
                <span>Created {new Date(note.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                {note.updated_at !== note.created_at && (
                  <><span>·</span><span>Updated {new Date(note.updated_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span></>
                )}
                {settings?.show_view_count && note.view_count > 0 && (
                  <><span>·</span><Eye size={13} /><span>{note.view_count.toLocaleString()} views</span></>
                )}
              </div>
            </header>

            {isEditing ? (
              <div className="np-public-edit-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--np-text)' }}>Content</label>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: editCharColor }}>
                    {editCharCount.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters
                  </span>
                </div>
                <textarea
                  className="np-public-edit-textarea"
                  value={editContent}
                  onChange={(e) => {
                    if (stripHtml(e.target.value).length <= MAX_CHARS) {
                      setEditContent(e.target.value);
                      setSaveError('');
                    } else {
                      setSaveError(`Exceeds ${MAX_CHARS} characters`);
                    }
                  }}
                  rows={14}
                  placeholder="Type note content here..."
                />
                <div className="np-char-bar" style={{ marginTop: 6 }}>
                  <div className="np-char-fill" style={{ width: `${editCharPercent}%`, background: editCharColor }} />
                </div>

                {saveError && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, color: '#ef4444', fontSize: '0.84rem' }}>
                    <AlertTriangle size={14} /> {saveError}
                  </div>
                )}

                <div className="np-public-edit-actions" style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                  <button
                    type="button"
                    className="np-public-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : saveSuccess ? <><CheckCircle2 size={15} /> Saved!</> : <><Save size={15} /> Save Changes</>}
                  </button>
                  <button
                    type="button"
                    className="np-public-cancel-btn"
                    onClick={handleCancelEdit}
                    disabled={saving}
                  >
                    <X size={14} /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="np-public-content-body">
                <pre className="np-public-content">{plainContent || <em>No content</em>}</pre>
              </div>
            )}

            {!isEditing && (
              <footer className="np-public-footer">
                <div className="np-public-char-row">
                  <div className="np-public-char-bar">
                    <div className="np-public-char-fill" style={{ width: `${Math.min((charCount / MAX_CHARS) * 100, 100)}%` }} />
                  </div>
                  <span className="np-public-char-count">
                    {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {settings?.allow_copy && (
                    <button type="button" className="np-public-copy-btn" onClick={handleCopy}>
                      {copied ? <><CheckCircle2 size={14} /> Copied!</> : <><Copy size={14} /> Copy Link</>}
                    </button>
                  )}
                  {settings?.allow_edit && (
                    <button type="button" className="np-public-copy-btn" onClick={() => setIsEditing(true)}>
                      <Edit3 size={14} /> Edit
                    </button>
                  )}
                </div>
              </footer>
            )}
          </article>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`np-public-page ${darkMode ? 'np-dark' : 'np-light'}`}>
      {/* Minimal Public Header */}
      <header className="np-public-header">
        <div className="np-public-brand">
          <StickyNote size={20} color="#6366f1" />
          <span className="np-public-brand-name">CipherVault Notepad</span>
        </div>
        <button type="button" className="np-public-theme-toggle" onClick={() => setDarkMode(!darkMode)} aria-label="Toggle theme">
          {darkMode ? <Sun size={18} /> : <Moon size={18} />}
        </button>
      </header>

      {/* Content */}
      <main className="np-public-main">
        {renderContent()}
      </main>

      {/* Minimal Public Footer */}
      <footer className="np-public-page-footer">
        <p>Powered by <strong>CipherVault</strong> · End-to-End Encrypted</p>
      </footer>
    </div>
  );
}
